import { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipForward, SkipBack, Shuffle, X, ChevronUp, ChevronDown, Music4, GripVertical, EyeOff } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { streamAsset } from "@/api/mist";

function CoverArt({ coverId, size }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    setSrc(null);
    if (!coverId) return;
    streamAsset(coverId).then((u) => { url = u; setSrc(u); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [coverId]);
  return (
    <div className="shrink-0 rounded-md overflow-hidden bg-gradient-to-br from-[#00E5C0] to-[#A855F7] grid place-items-center" style={{ width: size, height: size }}>
      {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : <Music4 size={size * 0.42} className="text-black/70" />}
    </div>
  );
}

export default function MiniPlayer() {
  const p = usePlayer();
  const barRef = useRef(null);
  const drag = useRef(null);
  const [pos, setPos] = useState(() => {
    try { return JSON.parse(localStorage.getItem("mist_player_pos") || "null"); } catch { return null; }
  });
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (pos) { try { localStorage.setItem("mist_player_pos", JSON.stringify(pos)); } catch { /* ignore */ } }
  }, [pos]);

  if (!p || !p.hasQueue || !p.current) return null;
  const { current, playing, shuffle, progress, expanded } = p;
  const title = current.title || current.filename || "Untitled";

  const onGripDown = (e) => {
    const el = barRef.current;
    if (!el) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const rect = el.getBoundingClientRect();
    const parent = (el.offsetParent || document.body).getBoundingClientRect();
    drag.current = {
      px: e.clientX, py: e.clientY,
      left: rect.left - parent.left, top: rect.top - parent.top,
      pw: parent.width, ph: parent.height, w: rect.width, h: rect.height,
    };
  };
  const onGripMove = (e) => {
    const d = drag.current;
    if (!d) return;
    let nl = d.left + (e.clientX - d.px);
    let nt = d.top + (e.clientY - d.py);
    nl = Math.max(4, Math.min(nl, d.pw - d.w - 4));
    nt = Math.max(4, Math.min(nt, d.ph - d.h - 4));
    setPos({ left: nl, top: nt });
  };
  const onGripUp = () => { drag.current = null; };

  // Hidden → small edge tab that brings the player back.
  if (hidden) {
    return (
      <button onClick={() => setHidden(false)} data-testid="mini-player-show"
        className="absolute bottom-[72px] right-2 z-40 w-11 h-11 grid place-items-center rounded-full bg-[#00E5C0] border-2 border-black shadow-[2px_2px_0px_0px_#000] active:scale-90">
        <Music4 size={18} className={`text-black ${playing ? "animate-pulse" : ""}`} />
      </button>
    );
  }

  const floating = !!pos;
  const outerCls = `absolute z-40 ${floating ? "" : "left-0 right-0 bottom-[68px] px-2"}`;
  const outerStyle = floating ? { left: pos.left, top: pos.top, width: 392 } : undefined;

  return (
    <>
      {/* Compact bar */}
      <div ref={barRef} data-testid="mini-player" className={outerCls} style={outerStyle}>
        <div className="mx-auto max-w-[396px] bg-[#0d0d0d] border-2 border-[#00E5C0] rounded-xl shadow-[0_0_20px_rgba(0,229,192,0.35)] overflow-hidden">
          <div className="h-[3px] bg-white/10">
            <div className="h-full bg-[#00E5C0] transition-[width] duration-300" style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
          <div className="flex items-center gap-1 p-2">
            <button
              onPointerDown={onGripDown} onPointerMove={onGripMove} onPointerUp={onGripUp} onPointerCancel={onGripUp}
              data-testid="mini-player-drag" title="Drag to move" aria-label="Drag player"
              className="shrink-0 w-5 h-9 grid place-items-center text-white/40 touch-none cursor-grab active:cursor-grabbing">
              <GripVertical size={16} />
            </button>
            <button onClick={() => p.setExpanded(!expanded)} data-testid="mini-player-expand" className="flex items-center gap-2 min-w-0 flex-1 text-left">
              <CoverArt coverId={current.coverId} size={36} />
              <div className="min-w-0">
                <p className="font-mono text-[11px] text-white truncate">{title}</p>
                <p className="font-mono text-[8px] uppercase tracking-widest text-[#00E5C0] truncate">{current.genre || "PLAYING"} · {p.position + 1}/{p.queueLength}</p>
              </div>
            </button>
            <button onClick={p.prev} data-testid="mini-player-prev" className="shrink-0 w-7 h-8 grid place-items-center text-white/80 active:scale-90"><SkipBack size={15} /></button>
            <button onClick={p.toggle} data-testid="mini-player-toggle" className="shrink-0 w-9 h-9 grid place-items-center rounded-full bg-[#00E5C0] text-black active:scale-90">{playing ? <Pause size={16} /> : <Play size={16} />}</button>
            <button onClick={p.next} data-testid="mini-player-next" className="shrink-0 w-7 h-8 grid place-items-center text-white/80 active:scale-90"><SkipForward size={15} /></button>
            <button onClick={() => setHidden(true)} data-testid="mini-player-hide" title="Hide player" className="shrink-0 w-7 h-8 grid place-items-center text-white/60 active:scale-90"><EyeOff size={15} /></button>
            <button onClick={p.close} data-testid="mini-player-close" title="Close player" className="shrink-0 w-7 h-8 grid place-items-center text-[#FF4D6D] active:scale-90"><X size={16} /></button>
          </div>
        </div>
      </div>

      {/* Expanded full player */}
      {expanded && (
        <div data-testid="mini-player-full" className="absolute inset-0 z-50 bg-black flex flex-col items-center justify-center px-8 gap-6">
          <button onClick={() => p.setExpanded(false)} data-testid="mini-player-collapse" className="absolute top-4 right-4 w-10 h-10 grid place-items-center rounded-full border-2 border-white/30 text-white"><ChevronDown size={20} /></button>
          <button onClick={p.close} data-testid="mini-player-close-full" className="absolute top-4 left-4 w-10 h-10 grid place-items-center rounded-full border-2 border-[#FF4D6D] text-[#FF4D6D]"><X size={18} /></button>
          <CoverArt coverId={current.coverId} size={220} />
          <div className="text-center">
            <p className="font-serif text-2xl text-white leading-tight">{title}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#00E5C0] mt-1">{current.genre || "NOW PLAYING"} · {p.position + 1} of {p.queueLength}</p>
          </div>
          <div className="w-full max-w-[320px]">
            <div className="h-1.5 bg-white/10 rounded-full cursor-pointer" onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); p.seek((e.clientX - r.left) / r.width); }} data-testid="mini-player-seek">
              <div className="h-full bg-[#00E5C0] rounded-full" style={{ width: `${Math.round(progress * 100)}%` }} />
            </div>
          </div>
          <div className="flex items-center gap-6">
            <button onClick={p.toggleShuffle} data-testid="mini-player-shuffle" className={`w-11 h-11 grid place-items-center rounded-full border-2 ${shuffle ? "bg-[#00E5C0] text-black border-[#00E5C0]" : "text-white/70 border-white/30"}`}><Shuffle size={18} /></button>
            <button onClick={p.prev} className="w-12 h-12 grid place-items-center text-white active:scale-90"><SkipBack size={26} /></button>
            <button onClick={p.toggle} className="w-16 h-16 grid place-items-center rounded-full bg-[#00E5C0] text-black active:scale-90">{playing ? <Pause size={28} /> : <Play size={28} />}</button>
            <button onClick={p.next} className="w-12 h-12 grid place-items-center text-white active:scale-90"><SkipForward size={26} /></button>
            <div className="w-11" />
          </div>
        </div>
      )}
    </>
  );
}
